/**
 * Challenge hero section — cover image/gradient/emoji, name, status badge,
 * creator row, description, tags, stats row, dates, gender filter.
 */

import { useState } from 'react'
import { useTranslation } from 'react-i18next'
import { useNavigate } from 'react-router-dom'
import {
  Clock,
  Star,
  ListChecks,
  Calendar,
  ExternalLink,
  ChevronDown,
  ChevronUp,
  ChevronRight,
} from 'lucide-react'
import { motion } from 'framer-motion'

import { AvatarWithBadge, getBadgeDisplay } from '@turnifly/shared/ui'
import { Badge } from '@turnifly/shared/ui'
import type {
  ChallengeDetail,
  ChallengeProfileCreator,
  MemberPreview,
} from '@turnifly/shared/types'

// ── Props ────────────────────────────────────────────────────

interface ChallengeCoverSectionProps {
  challenge: ChallengeDetail
  creator: ChallengeProfileCreator
  membersPreview: MemberPreview[]
  habitsCount: number
  isCreator: boolean
}

// ── Status badge config ──────────────────────────────────────

const STATUS_CFG: Record<string, { variant: 'success' | 'warning' | 'primary' | 'danger' | 'glass' }> = {
  active: { variant: 'success' },
  upcoming: { variant: 'warning' },
  completed: { variant: 'primary' },
  cancelled: { variant: 'danger' },
  draft: { variant: 'glass' },
}

// ── Cover background ─────────────────────────────────────────

function CoverBackground({ challenge }: { challenge: ChallengeDetail }): React.ReactElement {
  if (challenge.cover_image_url) {
    return (
      <div className="relative h-40 w-full overflow-hidden rounded-b-3xl lg:h-48">
        <img src={challenge.cover_image_url} alt="" className="h-full w-full object-cover" />
        <div className="absolute inset-0 bg-gradient-to-t from-[var(--color-bg)] via-[var(--color-bg)]/40 to-transparent" />
      </div>
    )
  }

  if (challenge.cover_gradient) {
    return (
      <div className={`relative h-40 w-full overflow-hidden rounded-b-3xl lg:h-48 ${challenge.cover_gradient}`}>
        <div className="absolute inset-0 bg-gradient-to-t from-[var(--color-bg)] via-transparent to-transparent" />
      </div>
    )
  }

  return (
    <div className="relative flex h-40 w-full items-center justify-center overflow-hidden rounded-b-3xl bg-gradient-to-br from-[var(--color-primary)]/20 via-[var(--color-accent)]/10 to-transparent lg:h-48">
      <span className="text-6xl drop-shadow-lg">{challenge.cover_emoji ?? '🏆'}</span>
      <div className="absolute inset-0 bg-gradient-to-t from-[var(--color-bg)] via-transparent to-transparent" />
    </div>
  )
}

// ── Member avatars stack ─────────────────────────────────────

function MemberAvatarStack({ members, total, t }: {
  members: MemberPreview[]
  total: number
  t: (key: string) => string
}): React.ReactElement | null {
  if (members.length === 0) return null

  const visible = members.slice(0, 5)
  const remaining = Math.max(0, total - visible.length)

  return (
    <div className="flex items-center gap-2">
      <div className="flex -space-x-2.5">
        {visible.map((m) => (
          <img
            key={m.id}
            src={m.avatar_url ?? undefined}
            alt=""
            className="h-7 w-7 rounded-full border-2 border-[var(--color-bg)] bg-[var(--color-surface)] object-cover"
            loading="lazy"
          />
        ))}
      </div>
      {remaining > 0 && (
        <span className="text-[11px] font-medium text-[var(--color-text-hint)]">
          +{remaining} {t('challenges.profile.more')}
        </span>
      )}
    </div>
  )
}

// ── Stat item ────────────────────────────────────────────────

function StatItem({ icon, value, label }: {
  icon: React.ReactNode
  value: string
  label: string
}): React.ReactElement {
  return (
    <div className="flex flex-1 flex-col items-center gap-0.5">
      <div className="flex items-center gap-1 text-white">
        {icon}
        <span className="text-sm font-bold tabular-nums">{value}</span>
      </div>
      <span className="text-[10px] text-[var(--color-text-hint)]">{label}</span>
    </div>
  )
}

// ── Main component ───────────────────────────────────────────

export function ChallengeCoverSection({
  challenge,
  creator,
  membersPreview,
  habitsCount,
  isCreator,
}: ChallengeCoverSectionProps): React.ReactElement {
  const { t, i18n } = useTranslation()
  const navigate = useNavigate()
  const [isDescExpanded, setDescExpanded] = useState(false)

  const lang = i18n.language
  const stCfg = STATUS_CFG[challenge.status] ?? STATUS_CFG.upcoming

  const hasDesc = !!challenge.description && challenge.description.length > 0
  const isDescLong = hasDesc && challenge.description!.length > 120

  const fmtDate = (ds: string | null): string => {
    if (!ds) return '—'
    const d = new Date(ds)
    return d.toLocaleDateString(lang === 'uz' ? 'uz-UZ' : 'en-US', {
      day: '2-digit',
      month: '2-digit',
      year: 'numeric',
    })
  }

  const ratingText =
    challenge.avg_rating != null && challenge.reviews_count >= 10
      ? challenge.avg_rating.toFixed(1)
      : '—'

  return (
    <motion.div
      initial={{ opacity: 0, y: 8 }}
      animate={{ opacity: 1, y: 0 }}
      transition={{ duration: 0.25 }}
    >
      <CoverBackground challenge={challenge} />

      {/* Content */}
      <div className="-mt-6 px-4">
        {/* Name + badges */}
        <h1 className="text-xl font-bold text-white leading-tight">{challenge.name}</h1>
        <div className="mt-1.5 flex flex-wrap items-center gap-1.5">
          <Badge variant={stCfg.variant}>{t(`challenges.status.${challenge.status}`)}</Badge>
          {challenge.gender_filter !== 'all' && (
            <Badge variant="glass">{t(`challenges.gender.${challenge.gender_filter}`)}</Badge>
          )}
          {challenge.is_paid ? (
            <Badge variant="streak">
              {(challenge.price ?? 0).toLocaleString()} so&apos;m
            </Badge>
          ) : (
            <Badge variant="success">{t('common.free')}</Badge>
          )}
        </div>

        {/* Creator row */}
        <button
          type="button"
          className="mt-3 flex w-full items-center gap-2.5 rounded-xl py-1.5 tap-highlight-transparent"
          onClick={() => navigate(`/profile/${creator.id}`)}
        >
          <AvatarWithBadge
            src={creator.avatar_url}
            fallback={creator.full_name}
            size="sm"
            badge={getBadgeDisplay(creator.is_premium, creator.badge_type)}
          />
          <div className="flex-1 text-left">
            <span className="text-[13px] font-semibold text-white">{creator.full_name}</span>
            <span className="ml-1.5 text-[11px] text-[var(--color-text-hint)]">
              · {t('challenges.profile.creator')}
            </span>
          </div>
          {isCreator ? (
            <Badge variant="primary">{t('challenges.profile.you')}</Badge>
          ) : (
            <ChevronRight size={14} className="text-[var(--color-text-hint)]/40" />
          )}
        </button>

        {/* Description */}
        {hasDesc && (
          <div className="mt-2">
            <p className={`text-[13px] leading-relaxed text-white/70 ${!isDescExpanded && isDescLong ? 'line-clamp-2' : ''}`}>
              {challenge.description}
            </p>
            {isDescLong && (
              <button
                type="button"
                className="mt-0.5 flex items-center gap-0.5 text-[12px] font-medium text-[var(--color-primary)]"
                onClick={() => setDescExpanded((v) => !v)}
              >
                {isDescExpanded ? t('common.close') : t('challenges.profile.more')}
                {isDescExpanded ? <ChevronUp size={12} /> : <ChevronDown size={12} />}
              </button>
            )}
          </div>
        )}

        {/* Link */}
        {challenge.link_url && (
          <a
            href={challenge.link_url}
            target="_blank"
            rel="noopener noreferrer"
            className="mt-2 inline-flex items-center gap-1 text-[12px] font-medium text-[var(--color-primary)]"
          >
            <ExternalLink size={12} />
            {challenge.link_title || challenge.link_url}
          </a>
        )}

        {/* Tags */}
        {challenge.tags.length > 0 && (
          <div className="mt-2 flex flex-wrap gap-1.5">
            {challenge.tags.map((tag) => (
              <span
                key={tag}
                className="rounded-full bg-[var(--color-primary)]/10 px-2.5 py-0.5 text-[11px] font-medium text-[var(--color-primary)]"
              >
                #{tag}
              </span>
            ))}
          </div>
        )}

        {/* Stats row */}
        <div className="mt-4 flex items-center rounded-2xl bg-white/[0.04] px-2 py-3">
          <div className="flex flex-1 flex-col items-center gap-1">
            <MemberAvatarStack members={membersPreview} total={challenge.members_count} t={t} />
            <span className="text-[10px] text-[var(--color-text-hint)]">
              {challenge.members_count} {t('challenges.profile.members')}
            </span>
          </div>
          <div className="h-6 w-px bg-white/[0.08]" />
          <StatItem
            icon={<Clock size={13} className="text-[var(--color-accent)]" />}
            value={`${challenge.duration_days}`}
            label={t('challenges.days')}
          />
          <div className="h-6 w-px bg-white/[0.08]" />
          <StatItem
            icon={<ListChecks size={13} className="text-[var(--color-warning)]" />}
            value={`${habitsCount}`}
            label={t('challenges.profile.tasks')}
          />
          <div className="h-6 w-px bg-white/[0.08]" />
          <StatItem
            icon={<Star size={13} className="text-yellow-400" />}
            value={ratingText}
            label={t('challenges.profile.rating')}
          />
        </div>

        {/* Dates */}
        <div className="mt-3 flex items-center gap-2 pb-1 text-[12px] text-[var(--color-text-hint)]">
          <Calendar size={13} />
          {challenge.time_model === 'evergreen' ? (
            <span>{t('challenges.profile.timeModelEvergreen')}</span>
          ) : (
            <span>{fmtDate(challenge.start_date)} — {fmtDate(challenge.end_date)}</span>
          )}
        </div>
      </div>
    </motion.div>
  )
}
